/**
 * Normalize ACF section groups for course detail accordion.
 *
 * Quiz model: ONE quiz per course (`acf.related_quiz`). A section is gated on it when
 * the section's `q_nums` field (comma-separated question positions) is set. There is
 * no more per-section quiz field.
 */

export const COURSE_SECTION_KEYS = ['section_intro', 'section_1', 'section_2', 'section_3', 'section_4', 'section_5'];

const EPISODE_KEYS_INTRO = ['episod_1'];
const EPISODE_KEYS_FULL = ['episod_1', 'episod_2', 'episod_3', 'episod_4', 'episod_5', 'episod_6', 'episod_7', 'episod_8'];

const TITLE_KEYS_INTRO = ['title_1'];
const TITLE_KEYS_FULL = ['title_1', 'title_2', 'title_3', 'title_4', 'title_5', 'title_6', 'title_7', 'title_8'];

/**
 * @param {unknown} raw
 * @returns {number|null}
 */
export function resolveQuizPostId(raw) {
    if (raw === false || raw === null || raw === undefined || raw === '') return null;
    if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) return Math.floor(raw);
    if (typeof raw === 'string') {
        const n = parseInt(raw, 10);
        return Number.isFinite(n) && n > 0 ? n : null;
    }
    if (typeof raw === 'object') {
        const id = raw.ID ?? raw.id ?? raw.post_id;
        if (typeof id === 'number' && id > 0) return Math.floor(id);
        if (typeof id === 'string') {
            const n = parseInt(id, 10);
            return Number.isFinite(n) && n > 0 ? n : null;
        }
    }
    return null;
}

/**
 * Sections no longer carry their own quiz reference (section_intro_quiz / section_quiz
 * are retired). There is ONE quiz per course — the top-level `related_quiz` field — and
 * a section is gated on it only when the section's `q_nums` (comma-separated question
 * positions, e.g. "1,2") is set. Final pass/lock truth still comes from the server
 * (/courses/{id}/sections), which validates those positions actually exist in the quiz.
 *
 * @param {Record<string, unknown>} group
 * @param {number|null} courseQuizId
 * @returns {number|null}
 */
export function getSectionQuizId(group, courseQuizId) {
    if (!group || typeof group !== 'object' || !courseQuizId) return null;
    const qNums = /** @type {Record<string, unknown>} */ (group).q_nums;
    const hasQNums = typeof qNums === 'string' ? qNums.trim() !== '' : qNums != null && qNums !== '';
    return hasQNums ? courseQuizId : null;
}

/**
 * @param {unknown} v
 * @returns {string}
 */
function trimUrl(v) {
    if (typeof v !== 'string') return '';
    return v.trim();
}

/**
 * ACF WYSIWYG "reading" field, right before q_nums in each section group — a reading
 * lesson the user must confirm (honor-code checkbox) before that section's quiz.
 * Empty per courseSections.js's own convention (skip if empty) means TinyMCE's
 * "<p>&nbsp;</p>"-style empty output too, not just a literal empty string.
 *
 * @param {unknown} v
 * @returns {string} the raw HTML, or '' if there's no meaningful content
 */
function trimReading(v) {
    if (typeof v !== 'string') return '';
    const text = v.replace(/&nbsp;/g, ' ').replace(/<[^>]*>/g, '').trim();
    return text !== '' ? v.trim() : '';
}

/**
 * @param {string} sectionKey
 * @param {Record<string, unknown>} group
 * @returns {{ key: string, url: string, title?: string }[]}
 */
export function getEpisodesForSection(sectionKey, group) {
    if (!group || typeof group !== 'object') return [];
    const g = /** @type {Record<string, unknown>} */ (group);
    const keys = sectionKey === 'section_intro' ? EPISODE_KEYS_INTRO : EPISODE_KEYS_FULL;
    const titleKeys = sectionKey === 'section_intro' ? TITLE_KEYS_INTRO : TITLE_KEYS_FULL;
    const out = [];
    for (let i = 0; i < keys.length; i++) {
        const k = keys[i];
        const url = trimUrl(g[k]);
        if (!url) continue;
        const rawTitle = g[titleKeys[i]];
        const title = typeof rawTitle === 'string' && rawTitle.trim() ? rawTitle.trim() : undefined;
        out.push({ key: k, url, title });
    }
    return out;
}

/**
 * @param {string} sectionKey
 * @param {{ key: string, url: string }[]} episodes
 * @param {number|null} quizId
 * @param {string} rawName
 * @returns {string}
 */
function resolveSectionTitle(sectionKey, episodes, quizId, rawName) {
    const name = typeof rawName === 'string' ? rawName.trim() : '';
    if (name) return name;
    if (episodes.length === 0 && !quizId) return '';
    if (sectionKey === 'section_intro') return 'مقدمة';
    const m = /^section_(\d+)$/.exec(sectionKey);
    if (m) return `قسم ${m[1]}`;
    return 'قسم';
}

/**
 * @param {unknown} course
 * @returns {{ key: string, title: string, episodes: { key: string, url: string, title?: string }[], quizId: number|null, reading: string }[]}
 */
export function getCourseSections(course) {
    const acf = course?.acf;
    if (!acf || typeof acf !== 'object') return [];

    const courseQuizId = resolveQuizPostId(acf.related_quiz);

    const sections = [];
    for (const sectionKey of COURSE_SECTION_KEYS) {
        const group = acf[sectionKey];
        if (!group || typeof group !== 'object') continue;

        const episodes = getEpisodesForSection(sectionKey, /** @type {Record<string, unknown>} */ (group));
        const quizId = getSectionQuizId(/** @type {Record<string, unknown>} */ (group), courseQuizId);
        const reading = trimReading(/** @type {Record<string, unknown>} */ (group).reading);
        const rawName =
            typeof /** @type {Record<string, unknown>} */ (group).section_name === 'string'
                ? /** @type {Record<string, unknown>} */ (group).section_name
                : '';

        const title = resolveSectionTitle(sectionKey, episodes, quizId, rawName);
        const hasContent = title !== '' || episodes.length > 0 || quizId != null || reading !== '';
        if (!hasContent) continue;

        sections.push({
            key: sectionKey,
            title: title || 'قسم',
            episodes,
            quizId,
            reading,
        });
    }
    return sections;
}

/**
 * @param {{ episodes: { key: string, url: string }[] }[]} sections
 * @returns {number}
 */
export function countTotalEpisodes(sections) {
    if (!Array.isArray(sections)) return 0;
    return sections.reduce((acc, s) => acc + (s.episodes?.length || 0), 0);
}

/**
 * @param {unknown} course
 * @returns {string}
 */
export function getInstructorDisplayName(course) {
    const inst = course?.acf?.instructor;
    if (inst && typeof inst === 'object' && typeof inst.post_title === 'string' && inst.post_title.trim()) {
        return inst.post_title.trim();
    }
    const author = course?._embedded?.author?.[0]?.name;
    if (typeof author === 'string' && author.trim()) return author.trim();
    return 'فيجماوية';
}
